// i18n.ts

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import translations from "../../assets/locales/translations.json";

// Convert file-based translations to i18next format
const convertTranslations = (translations: any, lang: string) => {
    const result: Record<string, any> = {};

    // Helper function to flatten nested objects
    const flattenTranslations = (obj: any, prefix = "") => {
        Object.entries(obj).forEach(([key, value]: [string, any]) => {
            const newKey = prefix ? `${prefix}.${key}` : key;

            // If the value is an object with language keys (en, es, etc.)
            if (value && typeof value === "object" && "en" in value) {
                // This is a leaf node with translations
                result[newKey] = value[lang] || value["en"] || key;
            }
            // If it's another nested object, recurse deeper
            else if (value && typeof value === "object") {
                flattenTranslations(value, newKey);
            }
        });
    };

    flattenTranslations(translations);
    return result;
};

// Create resources object for each language
const resources = {
    en: {
        translation: convertTranslations(translations, "en"),
    },
    es: {
        translation: convertTranslations(translations, "es"),
    },
    "pt-br": {
        translation: convertTranslations(translations, "pt-br"),
    },
    id: {
        translation: convertTranslations(translations, "id"),
    },
    th: {
        translation: convertTranslations(translations, "th"),
    },
};

// Apply language changes to the DOM
const applyLanguageToDOM = (lang: string) => {
    console.log(`Applying language "${lang}" to DOM`);
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.classList.remove(
        "lang-en",
        "lang-es",
        "lang-pt-br",
        "lang-id",
        "lang-th",
    );
    document.documentElement.classList.add(`lang-${lang}`);
};

// Always use language detection instead of checking LocalStateManager
console.log("Using language detection");
const initOptions = {
    resources,
    fallbackLng: "en",
    interpolation: {
        escapeValue: false,
    },
    supportedLngs: ["en", "es", "pt-br", "id", "th"],
    load: "currentOnly" as const,
    lowerCaseLng: true,
    react: {
        useSuspense: true,
    },
    // detection: {
    //     order: ["navigator"],
    //     caches: [],
    // },
    detection: {
        // inits from these, in order:
        order: ["localStorage", "navigator"],
        // and write back to…
        caches: ["localStorage"],
        // optional: customize the key name if you like:
        // lookupLocalStorage: "i18nextLng",
    },
};

// Event listeners
i18n.on("initialized", (obj) => {
    console.log("i18n initialized", obj);
    console.log(`Language initialized to: ${i18n.language}`);

    // Apply the current language to DOM
    applyLanguageToDOM(i18n.language);
});

i18n.on("languageChanged", (lang) => {
    console.log(`Language changed to: ${lang}`);

    // Always apply the new language to the DOM
    applyLanguageToDOM(lang);
});

i18n.on("loaded", () => {
    console.log("i18n resources loaded");
});

i18n.on("failedLoading", (lng, ns, msg) => {
    console.error(`Failed loading i18n: ${lng}, ${ns}, ${msg}`);
});

i18n.on("missingKey", (lng, ns, key) => {
    console.warn(`Missing i18n key: ${lng}, ${ns}, ${key}`);
});

// Initialize i18next with our specific configuration
i18n.use(initReactI18next).use(LanguageDetector).init(initOptions);

export default i18n;
